/* ============================================================
   VITALCASA CRM — ModalNota (tema claro/oscuro)
   Modal para crear notas y registrarlas en el historial.
   
   Props:
   - isOpen: boolean
   - onClose: () => void
   - onSave: ({ tipo, contenido }) => void
   - contact: { nombre, iniciales }
   ============================================================ */

import { useState, useRef } from 'react';
import { useTheme } from './ThemeProvider';

// ── Respuestas rápidas predefinidas ─────────────────────────
const QUICK_NOTES = [
  { id: 'no_contesta',   emoji: '📵', label: 'No contesta',         text: 'Se ha intentado contactar pero no contesta. Volver a llamar.' },
  { id: 'interesado',    emoji: '👍', label: 'Interesado',          text: 'El cliente muestra interés. Quiere más información o visitar propiedades.' },
  { id: 'no_interesado', emoji: '👎', label: 'No interesado',       text: 'El cliente no está interesado de momento. Dejar en seguimiento pasivo.' },
  { id: 'pide_tiempo',   emoji: '⏳', label: 'Pide tiempo',         text: 'El cliente pide que le demos tiempo para pensarlo. Contactar más adelante.' },
  { id: 'pide_docs',     emoji: '📋', label: 'Pide documentación',  text: 'El cliente solicita documentación adicional de las propiedades.' },
  { id: 'visita_ok',     emoji: '✅', label: 'Visita realizada',    text: 'Se ha realizado la visita correctamente.' },
  { id: 'oferta',        emoji: '💰', label: 'Ha hecho oferta',     text: 'El cliente ha realizado una oferta. Pendiente de respuesta del propietario.' },
  { id: 'urgente',       emoji: '🚨', label: 'Urgente',             text: 'Asunto urgente que requiere atención inmediata.' },
];

// ── Colores Nota (azul) ─────────────────────────────────────
const nt = {
  blue:       '#2563eb',
  blueFrom:   '#1d4ed8',
  blueTo:     '#2563eb',
  blueLight:  '#3b82f6',
  blueText:   '#dbeafe',
  blueShadow: 'rgba(37,99,235,0.35)',
  blueBg:     'rgba(37,99,235,0.08)',
  blueBorder: 'rgba(37,99,235,0.20)',
  chipText:   '#60a5fa',
};

export default function ModalNota({ isOpen, onClose, onSave, contact }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';

  const [contenido, setContenido] = useState('');
  const [guardado, setGuardado] = useState(false);
  const [msgFocused, setMsgFocused] = useState(false);

  // ── Drag del modal ───────────────────────────────────────────
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);
  const dragOffsetRef = useRef(null);
  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { ox: e.clientX - rect.left, oy: e.clientY - rect.top, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      setPos({ x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - ox)), y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy)) });
    };
    const onUp = () => { dragOffsetRef.current = null; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (!isOpen) return null;

  const applyQuickNote = (text) => {
    setContenido(prev => prev ? prev + '\n' + text : text);
  };

  const handleSave = () => {
    if (!contenido.trim()) return;

    onSave?.({
      tipo: 'nota',
      contenido: contenido.trim(),
    });

    setGuardado(true);
    setTimeout(() => {
      setGuardado(false);
      setContenido('');
      onClose?.();
    }, 1200);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div ref={dragRef} onClick={e => e.stopPropagation()} style={{
        width: 480, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
        boxShadow: isDark
          ? '0 0 60px 15px rgba(255,255,255,0.18), 0 0 120px 40px rgba(255,255,255,0.10), 0 0 200px 80px rgba(255,255,255,0.05), 0 0 300px 120px rgba(255,255,255,0.02)'
          : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
      }}>

        {/* Confirmación de guardado */}
        {guardado && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, zIndex: 2,
          }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: nt.blue }}>Nota guardada</div>
            <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500 }}>Registrada en el historial de {contact?.nombre}</div>
          </div>
        )}

        {/* Barra azul superior */}
        <div style={{
          height: 3, flexShrink: 0,
          background: `linear-gradient(90deg, ${nt.blueLight}, ${nt.blue}, ${nt.blueFrom}, ${nt.blue}, ${nt.blueLight})`,
        }} />

        {/* Header */}
        <div onMouseDown={startDrag} style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, cursor: 'grab', userSelect: 'none', background: isDark ? 'transparent' : '#e0e0d8', borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${nt.blueFrom}, ${nt.blueTo})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, border: `1px solid ${c.border}`,
              boxShadow: `0 4px 12px ${nt.blueShadow}`,
            }}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>Nueva nota</h2>
              <p style={{ margin: 0, marginTop: 1, fontSize: 12, fontWeight: 500, color: c.textMuted }}>
                {contact?.nombre || 'Cliente'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
            background: c.bgInput, cursor: 'pointer', fontSize: 16, color: c.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Cuerpo scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: isDark ? 0 : '4px 0 8px' }}>

          {/* Notas rápidas */}
          <div style={{ margin: '12px 12px 0', padding: '14px 16px', background: isDark ? 'transparent' : '#ffffff', borderRadius: 12, border: isDark ? 'none' : '1px solid #c8c8be', borderLeft: isDark ? 'none' : `4px solid ${nt.blue}`, boxShadow: isDark ? 'none' : '0 3px 10px rgba(0,0,0,0.12)' }}>
            <div style={{ display: 'block', marginBottom: 10, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: isDark ? c.textMuted : nt.blue }}>Notas rápidas</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {QUICK_NOTES.map(qn => (
                <button key={qn.id} onClick={() => applyQuickNote(qn.text)} style={{
                  padding: '6px 12px', borderRadius: 6,
                  border: `1px solid ${nt.blueBorder}`, background: nt.blueBg,
                  color: nt.chipText, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>{qn.emoji} {qn.label}</button>
              ))}
            </div>
          </div>

          {/* Contenido de la nota */}
          <div style={{ margin: '10px 12px 0', padding: '14px 16px', background: isDark ? 'transparent' : '#ffffff', borderRadius: 12, border: isDark ? 'none' : '1px solid #c8c8be', borderLeft: isDark ? 'none' : `4px solid ${nt.blue}`, boxShadow: isDark ? 'none' : '0 3px 10px rgba(0,0,0,0.12)' }}>
            <div style={{ display: 'block', marginBottom: 10, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: isDark ? c.textMuted : nt.blue }}>Nota</div>
            <textarea
              className="modal-nt-textarea"
              style={{
                width: '100%', minHeight: 140, maxHeight: 280,
                padding: '13px 16px', borderRadius: 10,
                border: `1.5px solid ${msgFocused ? nt.blue : (isDark ? c.border : '#a8a8a0')}`,
                background: isDark ? c.bgInput : 'rgba(37,99,235,0.10)', color: c.textPrimary,
                fontSize: 13.5, fontWeight: 500, fontFamily: 'inherit',
                outline: 'none', resize: 'vertical', transition: 'border-color 0.2s',
                lineHeight: 1.5,
              }}
              placeholder="Escribe tu nota aquí..."
              value={contenido}
              onChange={e => setContenido(e.target.value.slice(0, 5000))}
              onFocus={() => setMsgFocused(true)}
              onBlur={() => setMsgFocused(false)}
              autoFocus
            />
            <div style={{ textAlign: 'right', fontSize: 10.5, color: c.textMuted, fontWeight: 500, marginTop: 6, opacity: 0.5 }}>
              {contenido.length} / 5.000
            </div>
          </div>

          <div style={{ height: 8 }} />
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px 18px',
          display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
          flexShrink: 0, borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
          background: isDark ? 'transparent' : '#e0e0d8',
        }}>
          <button onClick={onClose} style={{
            padding: '11px 24px', borderRadius: 10, border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`,
            background: isDark ? c.bgInput : '#ffffff', color: c.textPrimary,
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          }}>Cancelar</button>
          <button onClick={handleSave} disabled={!contenido.trim()} style={{
            padding: '11px 30px', borderRadius: 10, border: 'none',
            background: contenido.trim() ? `linear-gradient(135deg, ${nt.blueFrom}, ${nt.blueTo})` : c.bgInput,
            color: contenido.trim() ? nt.blueText : c.textMuted,
            cursor: contenido.trim() ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
            boxShadow: contenido.trim() ? `0 4px 14px ${nt.blueShadow}` : 'none',
            display: 'flex', alignItems: 'center', gap: 8,
            opacity: contenido.trim() ? 1 : 0.5,
          }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
            Guardar nota
          </button>
        </div>

      </div>
    </div>
  );
}
